/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import useSelect from "~components/Form/hooks/useSelect";
import prepareForOptions from "~services/utils/prepareForOptions";
import {
  useGetCarteiraProspects,
  useGetCarteiraIdsProspects
} from "~/pages/Wallet/hooks/useWallet";
import { useLiveQuery } from "dexie-react-hooks";
import { liveQuery } from "dexie";
import { db } from "~/db/db";
import useWorker from "~hooks/useWorker";
import { saveLocalStorage } from "~services/utils/SaveIndexBD";
import { GetIndexBD } from "~services/utils/GetIndexBD";
import useVendedor from "~/fetchs/useVendedor";
import { validarProspectsConvertidos } from "~services/utils/validarProspectsConvertidos";

const useProspects = ({ formikRef, type, campo, restartFetch = true, skip, take = 6, setLoadingSearch = () => { } }) => {
  const { vendedorSelecionado, vendedorId, isVendedorReady } = useVendedor();
  const vendedorSelecionadoNormalizado = vendedorSelecionado ?? null;
  const shouldWaitForVendedor = !isVendedorReady;

  const [prospects, setProspects] = useState([]);
  const prospectSelect = useSelect("prospect");
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLoaded, setFilterLoaded] = useState(false);
  const [takeCarteira, setTakeCarteira] = useState(500);
  const [skipCarteira, setSkipCarteira] = useState(0);
  const prospectsCarteiraIds = useGetCarteiraIdsProspects();
  const prospectsCarteira = useGetCarteiraProspects(takeCarteira, skipCarteira, vendedorId, !shouldWaitForVendedor);
  const [loadingCached, setLoadingCached] = useState(false);

  const arrayOptionsCliente = ["codigo", "razaoSocial", "documento"];
  const arrayOptionsProspect = ["codigoProspect", "razaoSocial", "documento"];

  const normalizeString = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f\s]/g, "")
      .replace(/[./-]/g, "");

  const searchProspect = (prospect) => {
    return [
      (prospect.nome ?? "").toLowerCase(),
      (prospect.segmento ?? "").toLowerCase(),
      (prospect.grupoCentralizador ?? "").toLowerCase(),
      (prospect.documento ?? "").toLowerCase(),
      (prospect.codigoProspect ?? "").toLowerCase(),
    ].some((str) => normalizeString(str).includes(normalizeString(searchTerm.toLowerCase())));
  };

  useEffect(() => {
    let cancelled = false;

    const fetchClientes = async () => {
      if (shouldWaitForVendedor || !filterLoaded) {
        setProspects([]);
        setTotal(0);
        return;
      }

      let query = db.prospect.orderBy("razaoSocial");

      if (vendedorSelecionadoNormalizado) query = query.filter(c => c.usuarioId == vendedorSelecionadoNormalizado);

      if (type === "prospect")
        query = query.filter((c) => c.clienteProspect === 1 && c.ativo);
      else query = query.filter((c) => c.prospectCarteira === 1);

      if (searchTerm) {
        setLoadingSearch(true);
        query = query.filter(searchProspect);
      }

      query = await validarProspectsConvertidos(query);

      const count = await query.count();
      const results = [];

      if (type === "options" || type === "optionsProspect") {

        if (take === 0) {
          await query.each((item) => results.push(item));
        } else {
          await query.offset(0).limit(25).each((item) => results.push(item));
        }
      } else {
        await query.offset(0).limit(skip + take).each((item) => results.push(item));
      }

      if (cancelled) return;

      setTotal(count);
      setProspects(results);
      setLoadingSearch(false);
    };

    const subscription = liveQuery(fetchClientes).subscribe({
      error: () => {
        setLoadingSearch(false);
      },
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [skip, searchTerm, type, take, skip, vendedorSelecionadoNormalizado, shouldWaitForVendedor, filterLoaded]);

  const prospectsTotal = useLiveQuery(async () => {
    if (shouldWaitForVendedor) {
      return 0;
    }

    let query = db.prospect;

    query = query
      .where("clienteProspect")
      .equals(1)
      .and((e) => e.ativo);

    if (searchTerm) {
      query = query.and(searchProspect);
    }

    if (vendedorSelecionadoNormalizado) query = query.filter(c => c.usuarioId == vendedorSelecionadoNormalizado);
    query = await validarProspectsConvertidos(query);
    return query.count();
  }, [searchTerm, shouldWaitForVendedor, vendedorSelecionadoNormalizado]);

  const totalProspects = useLiveQuery(() => {
    return db.prospect.count();
  });

  const { save } = useWorker({
    table: "prospect",
  });

  const { save: saveCliente } = useWorker({
    table: "cliente",
  });

  function setBulk(data) {
    save(data);
    saveCliente(data);
  }

  function removeIds(data) {
    db.prospect.where("id").noneOf(data).delete()
  }

  const CarteiraIds = async () => {
    if (prospectsCarteiraIds.data && !prospectsCarteiraIds.isFetching) {
      let ids = (prospectsCarteiraIds.data || []).filter((e) => e.ativo).map((item) => item.id);
      removeIds(ids);
    }
  };

  const handleSearch = (inputText) => {
    setSearchTerm(inputText);
  };

  function onChangeCliente(e) {
    prospectSelect.upDateValue(e.value);
    prospectSelect.upDateDefaultValue(e || "");
    formikRef.current.setFieldValue(campo, e.value);
    setTimeout(() => formikRef.current.setFieldTouched(campo, true));
  }

  const options = useMemo(
    () =>
      prepareForOptions(prospects, type === "optionsProspect" ? arrayOptionsProspect : arrayOptionsCliente, "id"),
    [prospects]
  );

  useEffect(() => {
    if (shouldWaitForVendedor) {
      return;
    }

    if (prospectsCarteira.data && prospectsCarteira.data.length) {
      setBulk(prospectsCarteira.data);

      if (totalProspects < prospectsCarteira.total) {
        setSkipCarteira(skipCarteira + takeCarteira);
        prospectsCarteira.clearCache();
      }
    }
  }, [prospectsCarteira.data, prospectsCarteiraIds.data, shouldWaitForVendedor]);

  useEffect(() => {
    CarteiraIds();
  }, [prospectsCarteiraIds.data]);

  useEffect(() => {
    const loadIndexFilter = async () => {
      try {
        const data = await GetIndexBD("cadastro-prospect-filtro");
        const filter = data && typeof data == "object" ? data[type]?.filter : "";
        if (filter) setSearchTerm(filter);
      } finally {
        setFilterLoaded(true);
      }
    };

    loadIndexFilter();
  }, []);

  useEffect(() => {
    if (filterLoaded) updateIndexFilter();
  }, [searchTerm, filterLoaded]);

  const updateIndexFilter = async () => {
    let data = await GetIndexBD("cadastro-prospect-filtro");

    if (!data || typeof data != "object") {
      data = {};
    }

    data[type] = { ...(data[type] ?? {}), filter: searchTerm };

    saveLocalStorage("cadastro-prospect-filtro", data, true);
  };

  return {
    totalProspects,
    total,
    prospects,
    options,
    searchTerm,
    prospectSelect,
    onChangeCliente,
    handleSearch,
    loadingCached: loadingCached || shouldWaitForVendedor || !filterLoaded,
    loadingRequest: prospectsCarteira.isLoading,
  };
};

export default useProspects;
